import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSliders, FiX } = FiIcons;

const OpenerStyleSettings = ({ openerConfig, setOpenerConfig, selectedService, selectedSubService }) => {
  const [showManualInput, setShowManualInput] = useState(openerConfig?.focus === 'manual');

  // Handle focus change
  const handleFocusChange = (focusId) => {
    if (focusId === 'manual') {
      setShowManualInput(true);
    } else {
      setShowManualInput(false);
    }
    setOpenerConfig({ ...openerConfig, focus: focusId });
  };

  // Define different approaches based on service type
  const getServiceApproaches = () => {
    switch (selectedService) {
      case 'website':
        return {
          styles: [
            { id: 'casual', name: 'Casual', description: 'Friendly, conversational tone' },
            { id: 'professional', name: 'Professional', description: 'More formal and business-like' },
            { id: 'direct', name: 'Direct', description: 'Straight to the point' },
            { id: 'nonchalant', name: 'Nonchalant', description: 'Relaxed, seemingly effortless approach' },
            { id: 'curious', name: 'Curious', description: 'Inquisitive, question-based approach' }
          ],
          lengths: [
            { id: 'short', name: 'Short', description: '1-2 sentences' },
            { id: 'medium', name: 'Medium', description: '2-3 sentences' },
            { id: 'detailed', name: 'Detailed', description: '3-4 sentences' }
          ],
          focuses: [
            { id: 'refresh', name: 'Website Refresh', description: 'Focus on content refreshing' },
            { id: 'financing', name: '12-Month Financing', description: 'Focus on financing options' },
            { id: 'conversion', name: 'Conversion', description: 'Focus on improving conversion rates' },
            { id: 'mobile', name: 'Mobile Experience', description: 'Focus on mobile responsiveness' },
            { id: 'branding', name: 'Brand Consistency', description: 'Focus on content brand alignment' },
            { id: 'accessibility', name: 'Accessibility', description: 'Focus on inclusive content' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ]
        };
      case 'onboarding':
        return {
          styles: [
            { id: 'consultative', name: 'Consultative', description: 'Position as an expert consultant' },
            { id: 'educational', name: 'Educational', description: 'Focus on teaching platform benefits' },
            { id: 'results', name: 'Results-Driven', description: 'Emphasize concrete outcomes' },
            { id: 'casual', name: 'Casual', description: 'Friendly, conversational approach' },
            { id: 'nonchalant', name: 'Nonchalant', description: 'Relaxed, low-pressure style' }
          ],
          lengths: [
            { id: 'short', name: 'Short', description: '1-2 sentences' },
            { id: 'medium', name: 'Medium', description: '2-3 sentences' },
            { id: 'detailed', name: 'Detailed', description: '3-4 sentences' }
          ],
          focuses: selectedSubService === 'apollo' ? [
            { id: 'prospecting', name: 'Prospecting', description: 'Focus on finding leads' },
            { id: 'engagement', name: 'Engagement', description: 'Focus on outreach capabilities' },
            { id: 'analytics', name: 'Analytics', description: 'Focus on data and insights' },
            { id: 'integration', name: 'Integration', description: 'Focus on workflow integration' },
            { id: 'efficiency', name: 'Efficiency', description: 'Focus on time-saving features' },
            { id: 'targeting', name: 'Targeting', description: 'Focus on audience segmentation' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ] : [
            { id: 'deliverability', name: 'Deliverability', description: 'Focus on cold email deliverability' },
            { id: 'automation', name: 'Automation', description: 'Focus on outreach automation' },
            { id: 'scaling', name: 'Scaling', description: 'Focus on scaling cold outreach' },
            { id: 'personalization', name: 'Personalization', description: 'Focus on message customization' },
            { id: 'analytics', name: 'Analytics', description: 'Focus on performance tracking' },
            { id: 'compliance', name: 'Compliance', description: 'Focus on email regulations' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ]
        };
      case 'cold_email':
        return {
          styles: [
            { id: 'personable', name: 'Personable', description: 'Warm, personal approach' },
            { id: 'strategic', name: 'Strategic', description: 'Focus on business strategy' },
            { id: 'technical', name: 'Technical', description: 'Highlight technical expertise' },
            { id: 'casual', name: 'Casual', description: 'Friendly, conversational tone' },
            { id: 'nonchalant', name: 'Nonchalant', description: 'Relaxed, low-pressure approach' }
          ],
          lengths: [
            { id: 'short', name: 'Short', description: '1-2 sentences' },
            { id: 'medium', name: 'Medium', description: '2-3 sentences' },
            { id: 'detailed', name: 'Detailed', description: '3-4 sentences' }
          ],
          focuses: [
            { id: 'good_way_to_supplement_leads', name: 'Supplement Leads', description: 'Focus on complementing existing lead gen' },
            { id: 'targeting', name: 'Targeting', description: 'Focus on audience segmentation' },
            { id: 'high_roi', name: 'High ROI', description: 'Focus on return on investment' },
            { id: 'automation', name: 'Automation', description: 'Focus on time-saving workflows' },
            { id: 'personalization', name: 'Personalization', description: 'Focus on customized messaging' },
            { id: 'analytics', name: 'Analytics', description: 'Focus on performance tracking' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ]
        };
      case 'aeo':
        return {
          styles: [
            { id: 'consultative', name: 'Consultative', description: 'Position as an AI optimization expert' },
            { id: 'technical', name: 'Technical', description: 'Focus on AI technical implementation' },
            { id: 'strategic', name: 'Strategic', description: 'Emphasize strategic AI visibility' },
            { id: 'casual', name: 'Casual', description: 'Friendly, conversational approach' },
            { id: 'educational', name: 'Educational', description: 'Focus on teaching AEO benefits' }
          ],
          lengths: [
            { id: 'short', name: 'Short', description: '1-2 sentences' },
            { id: 'medium', name: 'Medium', description: '2-3 sentences' },
            { id: 'detailed', name: 'Detailed', description: '3-4 sentences' }
          ],
          focuses: [
            { id: 'ai_visibility', name: 'AI Visibility', description: 'Focus on AI search result rankings' },
            { id: 'content_structure', name: 'Content Structure', description: 'Focus on AI-friendly content formatting' },
            { id: 'semantic_optimization', name: 'Semantic SEO', description: 'Focus on entity and semantic markup' },
            { id: 'voice_search', name: 'Voice Search', description: 'Focus on voice and conversational queries' },
            { id: 'ai_snippets', name: 'AI Snippets', description: 'Focus on featured AI-generated content' },
            { id: 'chatbot_optimization', name: 'Chatbot Discovery', description: 'Focus on AI chatbot recommendations' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ]
        };
      case 'partnership':
        return {
          styles: [
            { id: 'consultative', name: 'Consultative', description: 'Position as a strategic partnership expert' },
            { id: 'collaborative', name: 'Collaborative', description: 'Emphasize mutual growth and teamwork' },
            { id: 'strategic', name: 'Strategic', description: 'Focus on long-term business strategy' },
            { id: 'professional', name: 'Professional', description: 'Formal, business-focused approach' },
            { id: 'casual', name: 'Casual', description: 'Friendly, conversational tone' }
          ],
          lengths: [
            { id: 'short', name: 'Short', description: '1-2 sentences' },
            { id: 'medium', name: 'Medium', description: '2-3 sentences' },
            { id: 'detailed', name: 'Detailed', description: '3-4 sentences' }
          ],
          focuses: [
            { id: 'team_augmentation', name: 'Team Augmentation', description: 'Focus on expanding development capacity' },
            { id: 'technical_expertise', name: 'Technical Expertise', description: 'Focus on specialized technical skills' },
            { id: 'project_delivery', name: 'Project Delivery', description: 'Focus on meeting deadlines and quality' },
            { id: 'cost_efficiency', name: 'Cost Efficiency', description: 'Focus on budget optimization' },
            { id: 'scalability', name: 'Scalability', description: 'Focus on growing development capabilities' },
            { id: 'long_term_partnership', name: 'Long-term Partnership', description: 'Focus on ongoing collaboration' },
            { id: 'website_management', name: 'Website Management', description: 'Need someone to manage their website' },
            { id: 'manual', name: 'Custom Focus', description: 'Enter your own focus area' }
          ]
        };
      default:
        return {
          styles: [],
          lengths: [],
          focuses: []
        };
    }
  };

  const serviceApproaches = getServiceApproaches();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="opener-settings"
    >
      <div className="settings-header">
        <div className="header-text">
          <h3>Opener Generation Settings</h3>
          <p>Configure how AI generates personalized openers for {
            selectedService === 'onboarding'
              ? `${selectedSubService === 'apollo' ? 'Apollo.io' : 'Instantly.ai'} onboarding`
              : selectedService === 'website' ? 'website redesign' 
              : selectedService === 'partnership' ? 'web development partnership'
              : selectedService === 'aeo' ? 'AI Engine Optimization'
              : 'cold email system'
          }</p>
        </div>
        <SafeIcon icon={FiSliders} className="settings-icon" />
      </div>

      <div className="settings-grid">
        <div className="setting-group">
          <label>Template Strategy</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="strategy"
                value="8step"
                checked={openerConfig.strategy === '8step' || !openerConfig.strategy}
                onChange={() => setOpenerConfig({ ...openerConfig, strategy: '8step' })}
              />
              <div className="radio-content">
                <span className="radio-title">8-Step Framework</span>
                <span className="radio-description">Comprehensive approach with detailed analysis</span>
              </div>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="strategy"
                value="5line"
                checked={openerConfig.strategy === '5line'}
                onChange={() => setOpenerConfig({ ...openerConfig, strategy: '5line' })}
              />
              <div className="radio-content">
                <span className="radio-title">5-Line Template</span>
                <span className="radio-description">Concise format with insight, value, proof, connection, and question</span>
              </div>
            </label>
          </div>
        </div>
        
        <div className="setting-group">
          <label>Approach Style</label>
          <div className="radio-group">
            {serviceApproaches.styles.map(style => (
              <label key={style.id} className="radio-label">
                <input
                  type="radio"
                  name="style"
                  value={style.id}
                  checked={openerConfig.style === style.id}
                  onChange={() => setOpenerConfig({ ...openerConfig, style: style.id })}
                />
                <div className="radio-content">
                  <span className="radio-title">{style.name}</span>
                  <span className="radio-description">{style.description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="setting-group">
          <label>Message Length</label>
          <div className="radio-group">
            {serviceApproaches.lengths.map(length => (
              <label key={length.id} className="radio-label">
                <input
                  type="radio"
                  name="length"
                  value={length.id}
                  checked={openerConfig.length === length.id}
                  onChange={() => setOpenerConfig({ ...openerConfig, length: length.id })}
                />
                <div className="radio-content">
                  <span className="radio-title">{length.name}</span>
                  <span className="radio-description">{length.description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="setting-group">
          <label>Focus Area</label>
          <div className="radio-group">
            {serviceApproaches.focuses.map(focus => (
              <label key={focus.id} className="radio-label">
                <input
                  type="radio"
                  name="focus"
                  value={focus.id}
                  checked={openerConfig.focus === focus.id}
                  onChange={() => handleFocusChange(focus.id)}
                />
                <div className="radio-content">
                  <span className="radio-title">{focus.name}</span>
                  <span className="radio-description">{focus.description}</span>
                </div>
              </label>
            ))}
          </div>
          
          {showManualInput && (
            <div className="manual-focus-input">
              <label>Enter Custom Focus Area</label>
              <input
                type="text"
                value={openerConfig.manualFocus || ''}
                onChange={(e) => setOpenerConfig({ ...openerConfig, manualFocus: e.target.value })}
                placeholder="e.g., content strategy, lead generation, etc."
                className="form-input"
              />
              <div className="input-help">
                Enter a specific aspect of their business you want to focus on
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .opener-settings {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }
        .header-text h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        .header-text p {
          font-size: 0.875rem;
          color: var(--text-light);
        }
        .settings-icon {
          font-size: 1.25rem;
          color: var(--primary-color);
          background-color: #f5f3ff;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
        }
        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }
        .setting-group {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .setting-group label {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-color);
        }
        .radio-group {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .radio-label {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.75rem;
          background-color: #f9fafb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .radio-label:hover {
          background-color: #f5f3ff;
        }
        .radio-label input {
          margin-top: 0.25rem;
        }
        .radio-content {
          display: flex;
          flex-direction: column;
        }
        .radio-title {
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        .radio-description {
          font-size: 0.75rem;
          color: var(--text-light);
        }
        .radio-label input:checked + .radio-content .radio-title {
          color: var(--primary-color);
        }
        .manual-focus-input {
          margin-top: 1rem;
          padding: 1rem;
          background-color: #f5f3ff;
          border-radius: 8px;
          border: 1px solid #e0e7ff;
        }
        .manual-focus-input label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .form-input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
        }
        .form-input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 2px rgba(99,102,241,0.2);
        }
        .input-help {
          font-size: 0.75rem;
          color: var(--text-light);
          margin-top: 0.25rem;
        }
        @media (max-width: 768px) {
          .settings-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default OpenerStyleSettings;